class ProductPurchaseController {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.productId = root.dataset.productId;
    this.productTitle = root.dataset.productTitle;
    this.settings = JSON.parse(root.querySelector('[data-product-settings]').textContent);
    this.variants = JSON.parse(root.querySelector('[data-product-variants]').textContent);
    this.state = {
      selections: [...JSON.parse(root.querySelector('[data-selected-options]').textContent)],
      quantity: Number(root.querySelector('[data-quantity-input]')?.value || 1),
    };

    this.purchaseAnchor = root.querySelector('[data-purchase-anchor]');
    this.inlineButtons = [...root.querySelectorAll('[data-option-button]')];
    this.optionSteps = [...root.querySelectorAll('[data-option-step]')];
    this.optionSummaries = [...root.querySelectorAll('[data-option-summary-value]')];
    this.summaryChips = root.querySelector('[data-option-summary]');
    this.priceCurrent = root.querySelector('[data-price-current]');
    this.priceCompare = root.querySelector('[data-price-compare]');
    this.inventoryMessage = root.querySelector('[data-inventory-message]');
    this.variantInput = root.querySelector('[data-variant-id-input]');
    this.mainAddButton = root.querySelector('[data-main-add-button]');
    this.mainBuyButton = root.querySelector('[data-main-buy-button]');
    this.sticky = root.querySelector('[data-sticky-purchase]');
    this.stickyButton = root.querySelector('[data-sticky-button]');
    this.stickySummary = root.querySelector('[data-sticky-summary]');
    this.stickyPrice = root.querySelector('[data-sticky-price]');
    this.drawer = root.querySelector('[data-option-drawer]');
    this.drawerCta = root.querySelector('[data-drawer-cta]');
    this.drawerBuyNow = root.querySelector('[data-drawer-buy-now]');
    this.drawerStepLabel = root.querySelector('[data-drawer-step]');
    this.quantityInput = root.querySelector('[data-quantity-input]');
    this.quantityButtons = [...root.querySelectorAll('[data-quantity-change]')];
    this.confirmationDrawer = root.querySelector('[data-added-drawer]');
    this.confirmationTitle = root.querySelector('[data-added-title]');
    this.confirmationMeta = root.querySelector('[data-added-meta]');
    this.confirmationImage = root.querySelector('[data-added-image]');
    this.cartCountNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.gallery = root.querySelector('.product-shell__gallery');
    this.mediaStage = root.querySelector('[data-product-media-stage]');
    this.mediaTrack = root.querySelector('[data-product-media-track]');
    this.mediaItems = [...root.querySelectorAll('[data-product-media-item][data-gallery-scope="hero"]')];
    this.thumbButtons = [...root.querySelectorAll('[data-media-thumb]')];
    this.prevMediaButton = root.querySelector('[data-media-prev]');
    this.nextMediaButton = root.querySelector('[data-media-next]');
    this.lightbox = root.querySelector('[data-product-lightbox]');
    this.lightboxItems = [...root.querySelectorAll('[data-lightbox-media-item]')];
    this.lightboxPrev = root.querySelector('[data-lightbox-prev]');
    this.lightboxNext = root.querySelector('[data-lightbox-next]');
    this.lightboxCloseButtons = [...root.querySelectorAll('[data-lightbox-close]')];
    this.galleryLayout = this.gallery?.dataset.galleryLayout || this.settings.gallery_layout || 'thumbnails_bottom';
    this.initialMediaStrategy = this.settings.initial_media_strategy || 'first_product_media';
    this.isCarouselLayout = this.galleryLayout === 'swipe_carousel';
    this.currentVariantId = null;
    this.currentMediaId =
      root.dataset.initialMediaId ||
      this.mediaItems.find((item) => item.classList.contains('is-active'))?.dataset.mediaId ||
      this.mediaItems[0]?.dataset.mediaId ||
      null;
    this.isBusy = false;

    this.handleSummaryEdit = this.handleSummaryEdit.bind(this);
    this.handleResize = this.refreshStickyEligibility.bind(this);
    this.handleTrackScroll = this.handleTrackScroll.bind(this);
    this.handleGalleryClick = this.handleGalleryClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.attachEvents();
    if (this.currentMediaId) {
      this.activateMedia(this.currentMediaId, { behavior: 'auto', syncLightbox: true });
    }
    this.setupObserver();
    window.addEventListener('resize', this.handleResize);
    this.syncState(true);
    window.AyuTheme.emitAnalytics('view_item', {
      product_id: this.productId,
      product_name: this.productTitle,
      variant_id: this.getCurrentVariant()?.id || '',
    });
  }

  attachEvents() {
    this.inlineButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.classList.contains('is-disabled')) return;
        const optionIndex = Number(button.dataset.optionIndex);
        this.selectOption(optionIndex, button.dataset.optionValue);
      });
    });

    this.drawerCta?.addEventListener('click', () => {
      if (this.isSelectionComplete() && this.getCurrentVariant()?.available) {
        this.addToCart({ closeOptions: true, source: 'option_drawer' });
        return;
      }

      const nextStep = this.getNextIncompleteStep();
      if (typeof nextStep === 'number') {
        this.focusStep(nextStep);
      }
    });

    this.drawerBuyNow?.addEventListener('click', () => {
      if (this.isSelectionComplete() && this.getCurrentVariant()?.available) {
        this.addToCart({
          closeOptions: true,
          source: 'option_drawer_buy_now',
          redirectToCheckout: true,
        });
        return;
      }

      const nextStep = this.getNextIncompleteStep();
      if (typeof nextStep === 'number') {
        this.focusStep(nextStep);
      }
    });

    this.mainAddButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.handlePrimaryAction('inline_form');
    });

    this.mainBuyButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleSecondaryAction('inline_buy_now');
    });

    this.stickyButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.handlePrimaryAction('sticky_purchase');
    });

    this.quantityInput?.addEventListener('change', () => {
      this.updateQuantity(this.quantityInput.value);
    });

    this.quantityInput?.addEventListener('input', () => {
      this.updateQuantity(this.quantityInput.value);
    });

    this.quantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (this.isBusy) return;
        this.updateQuantity(this.state.quantity + Number(button.dataset.quantityChange || 0));
      });
    });

    this.thumbButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.activateMedia(button.dataset.mediaId);
      });
    });

    this.prevMediaButton?.addEventListener('click', () => {
      this.handleMediaArrow(-1);
    });

    this.nextMediaButton?.addEventListener('click', () => {
      this.handleMediaArrow(1);
    });

    this.mediaTrack?.addEventListener('scroll', this.handleTrackScroll, { passive: true });
    this.mediaStage?.addEventListener('click', this.handleGalleryClick);

    this.lightboxPrev?.addEventListener('click', () => {
      this.handleMediaArrow(-1, 'lightbox');
    });

    this.lightboxNext?.addEventListener('click', () => {
      this.handleMediaArrow(1, 'lightbox');
    });

    this.lightboxCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.closeLightbox();
      });
    });

    document.addEventListener('product:summary-edit', this.handleSummaryEdit);
    document.addEventListener('keydown', this.handleKeydown);
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('product:summary-edit', this.handleSummaryEdit);
    document.removeEventListener('keydown', this.handleKeydown);
    this.mediaTrack?.removeEventListener('scroll', this.handleTrackScroll);
    this.mediaStage?.removeEventListener('click', this.handleGalleryClick);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  handleSummaryEdit(event) {
    if (event.detail.sectionId !== this.sectionId) return;
    this.focusStep(event.detail.optionIndex);
    window.AyuTheme.openDrawer(this.drawer);
    window.AyuTheme.emitAnalytics('sticky_purchase_open', {
      product_id: this.productId,
      product_name: this.productTitle,
      source: 'summary_chip',
      option_index: event.detail.optionIndex,
    });
  }

  handleKeydown(event) {
    if (!this.lightbox?.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      this.closeLightbox();
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.handleMediaArrow(-1, 'lightbox');
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.handleMediaArrow(1, 'lightbox');
    }
  }

  setupObserver() {
    if (!this.sticky || !this.purchaseAnchor) return;

    if (this.observer) {
      this.observer.disconnect();
    }

    if (!this.isStickyEnabledForViewport()) {
      this.sticky.classList.remove('is-visible');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.sticky.classList.remove('is-visible');
          } else {
            this.sticky.classList.add('is-visible');
          }
        });
      },
      {
        threshold: 0.25,
        rootMargin: `0px 0px -${this.settings.threshold}px 0px`,
      }
    );

    this.observer.observe(this.purchaseAnchor);
  }

  refreshStickyEligibility() {
    if (!this.sticky) return;

    if (!this.isStickyEnabledForViewport()) {
      this.sticky.classList.remove('is-visible');
      if (this.observer) this.observer.disconnect();
      return;
    }

    this.setupObserver();
  }

  isStickyEnabledForViewport() {
    if (window.matchMedia('(min-width: 75rem)').matches) return this.settings.enable_desktop;
    if (window.matchMedia('(min-width: 48rem)').matches) return this.settings.enable_tablet;
    return this.settings.enable_mobile;
  }

  handlePrimaryAction(source) {
    const variant = this.getCurrentVariant();

    if (!this.isSelectionComplete()) {
      this.focusStep(this.getNextIncompleteStep() || 0);
      window.AyuTheme.openDrawer(this.drawer);
      window.AyuTheme.emitAnalytics('sticky_purchase_open', {
        product_id: this.productId,
        product_name: this.productTitle,
        source,
      });
      return;
    }

    if (!variant || !variant.available) {
      return;
    }

    this.addToCart({ source });
  }

  handleSecondaryAction(source) {
    const variant = this.getCurrentVariant();

    if (!this.isSelectionComplete()) {
      this.focusStep(this.getNextIncompleteStep() || 0);
      window.AyuTheme.openDrawer(this.drawer);
      window.AyuTheme.emitAnalytics('sticky_purchase_open', {
        product_id: this.productId,
        product_name: this.productTitle,
        source,
      });
      return;
    }

    if (!variant || !variant.available) {
      return;
    }

    this.addToCart({ source, redirectToCheckout: true });
  }

  selectOption(optionIndex, optionValue) {
    this.state.selections[optionIndex] = optionValue;
    this.syncState();
    const optionStep = this.optionSteps[optionIndex];
    window.AyuTheme.emitAnalytics('option_step_complete', {
      product_id: this.productId,
      product_name: this.productTitle,
      option_index: optionIndex,
      option_name: optionStep?.dataset.optionName || '',
      option_value: optionValue,
    });

    const nextStep = this.getNextIncompleteStep();
    if (typeof nextStep === 'number') {
      this.focusStep(nextStep);
    } else {
      this.focusStep(this.optionSteps.length - 1);
    }
  }

  focusStep(stepIndex) {
    if (!this.optionSteps.length) return;
    this.optionSteps.forEach((step, index) => {
      step.classList.toggle('is-active', index === stepIndex);
      step.classList.toggle('is-complete', !!this.state.selections[index]);
    });

    if (this.drawerStepLabel) {
      this.drawerStepLabel.textContent = `Step ${stepIndex + 1} of ${this.optionSteps.length}`;
    }
  }

  isSelectionComplete() {
    return this.state.selections.every(Boolean);
  }

  getNextIncompleteStep() {
    return this.state.selections.findIndex((value) => !value);
  }

  getCurrentVariant() {
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === this.state.selections[index])
    );
  }

  getAvailableValues(optionIndex) {
    const partialSelections = [...this.state.selections];

    return this.variants
      .filter((variant) =>
        variant.options.every((value, index) => {
          if (index === optionIndex) return true;
          if (!partialSelections[index]) return true;
          return partialSelections[index] === value;
        })
      )
      .map((variant) => variant.options[optionIndex]);
  }

  getItemByMediaId(mediaId, scope = 'hero') {
    const items = scope === 'lightbox' ? this.lightboxItems : this.mediaItems;
    return items.find((item) => item.dataset.mediaId === String(mediaId));
  }

  getMediaIndex(mediaId, scope = 'hero') {
    const items = scope === 'lightbox' ? this.lightboxItems : this.mediaItems;
    return items.findIndex((item) => item.dataset.mediaId === String(mediaId));
  }

  pauseInteractiveMedia(container) {
    if (!container) return;
    container.querySelectorAll('video').forEach((node) => {
      if (typeof node.pause === 'function') node.pause();
    });
    container.querySelectorAll('model-viewer').forEach((node) => {
      if (typeof node.pause === 'function') node.pause();
    });
  }

  scrollMediaTrackToItem(item, behavior = 'smooth') {
    if (!this.mediaTrack || !item) return;
    item.scrollIntoView({
      behavior,
      inline: 'start',
      block: 'nearest',
    });
  }

  handleTrackScroll() {
    if (!this.isCarouselLayout || !this.mediaTrack || !this.mediaItems.length) return;
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

    this.scrollFrame = requestAnimationFrame(() => {
      const viewportCenter = this.mediaTrack.scrollLeft + this.mediaTrack.clientWidth / 2;
      let nextActive = this.mediaItems[0];
      let smallestDelta = Number.POSITIVE_INFINITY;

      this.mediaItems.forEach((item) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const delta = Math.abs(itemCenter - viewportCenter);
        if (delta < smallestDelta) {
          smallestDelta = delta;
          nextActive = item;
        }
      });

      if (nextActive?.dataset.mediaId && nextActive.dataset.mediaId !== this.currentMediaId) {
        this.activateMedia(nextActive.dataset.mediaId, {
          behavior: 'auto',
          fromScroll: true,
          syncLightbox: false,
        });
      }
    });
  }

  handleGalleryClick(event) {
    if (!this.settings.enable_lightbox || !this.lightbox) return;
    if (event.target.closest('[data-media-prev], [data-media-next], [data-media-thumb], button, a, input, summary')) return;
    this.openLightbox(this.currentMediaId);
  }

  handleMediaArrow(direction, scope = 'hero') {
    const items = scope === 'lightbox' ? this.lightboxItems : this.mediaItems;
    if (!items.length) return;

    const currentId = scope === 'lightbox'
      ? this.lightboxItems.find((item) => !item.hidden)?.dataset.mediaId || this.currentMediaId
      : this.currentMediaId;

    const currentIndex = Math.max(this.getMediaIndex(currentId, scope), 0);
    const nextIndex = (currentIndex + direction + items.length) % items.length;
    const nextMediaId = items[nextIndex]?.dataset.mediaId;

    if (!nextMediaId) return;

    if (scope === 'lightbox') {
      this.activateMedia(nextMediaId, {
        behavior: 'auto',
        syncLightbox: true,
      });
      return;
    }

    this.activateMedia(nextMediaId);
  }

  activateLightboxMedia(mediaId) {
    if (!mediaId || !this.lightboxItems.length) return;

    this.lightboxItems.forEach((item) => {
      const isActive = item.dataset.mediaId === String(mediaId);
      item.hidden = !isActive;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (!isActive) this.pauseInteractiveMedia(item);
    });
  }

  openLightbox(mediaId = this.currentMediaId) {
    if (!this.lightbox || !mediaId) return;
    this.activateLightboxMedia(mediaId);
    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    window.AyuTheme.lockScroll(true);
  }

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer.is-open')) {
      window.AyuTheme.lockScroll(false);
    }
  }

  activateMedia(mediaId, { behavior = 'smooth', fromScroll = false, syncLightbox = false } = {}) {
    if (!mediaId || !this.mediaItems.length) return;
    const targetItem = this.getItemByMediaId(mediaId, 'hero');
    if (!targetItem) return;

    this.currentMediaId = String(mediaId);

    this.mediaItems.forEach((item) => {
      const isActive = item.dataset.mediaId === String(mediaId);
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (this.isCarouselLayout) {
        item.hidden = false;
      } else {
        item.hidden = !isActive;
      }

      if (!isActive) this.pauseInteractiveMedia(item);
    });

    this.thumbButtons.forEach((button) => {
      button.classList.toggle('is-active', button.dataset.mediaId === String(mediaId));
      button.setAttribute('aria-pressed', button.dataset.mediaId === String(mediaId) ? 'true' : 'false');
    });

    if (this.isCarouselLayout && !fromScroll) {
      this.scrollMediaTrackToItem(targetItem, behavior);
    }

    if (syncLightbox) {
      this.activateLightboxMedia(mediaId);
    }
  }

  syncState(initial = false) {
    const availableValuesByIndex = this.optionSteps.map((_, optionIndex) =>
      this.getAvailableValues(optionIndex)
    );

    this.inlineButtons.forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      const isSelected = this.state.selections[optionIndex] === optionValue;
      const isAvailable = availableValuesByIndex[optionIndex].includes(optionValue);
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-disabled', !isAvailable);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button.disabled = !isAvailable;
    });

    this.optionSummaries.forEach((node) => {
      const optionIndex = Number(node.dataset.optionIndex);
      node.textContent = this.state.selections[optionIndex] || 'Not selected';
    });

    this.renderSummaryChips();

    const variant = this.getCurrentVariant();

    if (variant) {
      if (this.variantInput) this.variantInput.value = variant.id;
      if (this.priceCurrent) this.priceCurrent.textContent = window.AyuTheme.formatMoney(variant.price);
      if (this.priceCompare) {
        this.priceCompare.hidden = !variant.compare_at_price || variant.compare_at_price <= variant.price;
      }
      if (this.priceCompare && !this.priceCompare.hidden) {
        this.priceCompare.textContent = window.AyuTheme.formatMoney(variant.compare_at_price);
      }

      if (this.inventoryMessage && variant.inventory_message) {
        this.inventoryMessage.textContent = variant.inventory_message;
        this.inventoryMessage.hidden = false;
      } else if (this.inventoryMessage) {
        this.inventoryMessage.hidden = true;
      }

      if (variant.featured_media_id && (!initial || this.initialMediaStrategy === 'variant_query')) {
        this.activateMedia(variant.featured_media_id, {
          behavior: initial ? 'auto' : 'smooth',
          syncLightbox: true,
        });
      }
    }

    const primaryLabel = this.computePrimaryLabel(variant);
    const secondaryLabel = this.computeSecondaryLabel(variant);
    if (this.mainAddButton) {
      this.mainAddButton.textContent = primaryLabel;
      this.mainAddButton.disabled = primaryLabel === 'Sold out' || primaryLabel === 'Unavailable';
    }
    if (this.mainBuyButton) {
      this.mainBuyButton.textContent = secondaryLabel;
      this.mainBuyButton.disabled = secondaryLabel === 'Sold out' || secondaryLabel === 'Unavailable';
    }
    if (this.stickyButton) {
      this.stickyButton.textContent = primaryLabel;
      this.stickyButton.disabled = primaryLabel === 'Sold out' || primaryLabel === 'Unavailable';
    }
    if (this.drawerCta) {
      this.drawerCta.textContent = this.isSelectionComplete() ? primaryLabel : 'Continue';
    }
    if (this.drawerBuyNow) {
      this.drawerBuyNow.textContent = this.isSelectionComplete() ? secondaryLabel : (this.settings.buy_now_label || 'Buy now');
      this.drawerBuyNow.disabled = !this.isSelectionComplete() || secondaryLabel === 'Sold out' || secondaryLabel === 'Unavailable';
    }

    if (this.stickySummary) {
      const selectedSummary = this.state.selections.filter(Boolean).join(' / ');
      const fallbackSummary =
        this.optionSteps.length > 0
          ? this.settings.sticky_summary_default
          : variant?.title && variant.title !== 'Default Title'
            ? variant.title
            : '';
      const summaryText = selectedSummary || fallbackSummary;
      this.stickySummary.textContent = summaryText;
      this.stickySummary.hidden = !summaryText;
    }

    if (this.stickyPrice) {
      this.stickyPrice.textContent = variant ? window.AyuTheme.formatMoney(variant.price) : '';
      this.stickyPrice.hidden = !variant;
    }

    if (!initial && this.isSelectionComplete()) {
      window.history.replaceState({}, '', `${window.location.pathname}?variant=${variant?.id || ''}`);
    }

    this.focusStep(
      this.isSelectionComplete() ? this.optionSteps.length - 1 : Math.max(this.getNextIncompleteStep(), 0)
    );

    if (variant?.id && variant.id !== this.currentVariantId) {
      this.currentVariantId = variant.id;
      if (!initial) {
        window.AyuTheme.emitAnalytics('variant_change', {
          product_id: this.productId,
          product_name: this.productTitle,
          variant_id: variant.id,
          selections: [...this.state.selections],
        });
      }
    }

    this.root.dispatchEvent(
      new CustomEvent('product:state-change', {
        bubbles: true,
        detail: {
          sectionId: this.sectionId,
          selections: this.state.selections,
          variant,
        },
      })
    );
  }

  renderSummaryChips() {
    if (!this.summaryChips) return;

    this.summaryChips.innerHTML = '';

    this.optionSteps.forEach((step, index) => {
      const label = step.dataset.optionName;
      const value = this.state.selections[index];
      if (!value) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'summary-chip';
      button.textContent = `${label}: ${value}`;
      button.addEventListener('click', () => {
        document.dispatchEvent(
          new CustomEvent('product:summary-edit', {
            detail: { sectionId: this.sectionId, optionIndex: index },
          })
        );
      });
      this.summaryChips.appendChild(button);
    });
  }

  computePrimaryLabel(variant) {
    if (!this.isSelectionComplete()) {
      const nextIndex = this.getNextIncompleteStep();
      const activeStep = this.optionSteps[nextIndex];
      return activeStep ? `Select ${activeStep.dataset.optionName}` : 'Select options';
    }

    if (!variant) return 'Unavailable';
    if (!variant.available) return 'Sold out';
    return this.settings.atc_button_label || 'Add to bag';
  }

  computeSecondaryLabel(variant) {
    if (!this.isSelectionComplete()) {
      return this.settings.buy_now_label || 'Buy now';
    }

    if (!variant) return 'Unavailable';
    if (!variant.available) return 'Sold out';
    return this.settings.buy_now_label || 'Buy now';
  }

  updateQuantity(nextQuantity) {
    const parsed = Math.max(1, Number(nextQuantity || 1));
    this.state.quantity = Number.isFinite(parsed) ? parsed : 1;
    if (this.quantityInput) {
      this.quantityInput.value = this.state.quantity;
    }
  }

  async addToCart({ closeOptions = false, source = 'inline_form', redirectToCheckout = false } = {}) {
    const variant = this.getCurrentVariant();
    if (!variant?.id || this.isBusy) return;

    this.isBusy = true;
    if (this.mainAddButton) this.mainAddButton.textContent = 'Adding…';
    if (this.mainBuyButton) this.mainBuyButton.textContent = redirectToCheckout ? 'Redirecting…' : 'Adding…';
    if (this.stickyButton) this.stickyButton.textContent = 'Adding…';
    if (this.mainAddButton) this.mainAddButton.disabled = true;
    if (this.mainBuyButton) this.mainBuyButton.disabled = true;
    if (this.stickyButton) this.stickyButton.disabled = true;
    if (this.drawerCta) this.drawerCta.disabled = true;
    if (this.drawerBuyNow) this.drawerBuyNow.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: this.state.quantity }],
        }),
      });

      if (!response.ok) {
        this.syncState();
        return;
      }

      if (window.AyuTheme?.incrementCartCount) {
        window.AyuTheme.incrementCartCount(this.state.quantity);
      } else {
        this.updateCartCount(this.state.quantity);
      }
      this.populateConfirmation(variant);
      window.AyuTheme.emitAnalytics('add_to_cart', {
        product_id: this.productId,
        product_name: this.productTitle,
        variant_id: variant.id,
        quantity: this.state.quantity,
        source,
      });

      if (closeOptions) window.AyuTheme.closeDrawer(this.drawer);
      if (redirectToCheckout) {
        window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
      } else {
        window.AyuTheme.openDrawer(this.confirmationDrawer);
      }
      this.syncState();

      if (window.AyuTheme?.refreshCartCount) {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => window.AyuTheme.refreshCartCount());
        } else {
          window.setTimeout(() => window.AyuTheme.refreshCartCount(), 250);
        }
      }
    } finally {
      this.isBusy = false;
      this.syncState();
      if (this.drawerCta) this.drawerCta.disabled = false;
      if (this.drawerBuyNow) this.drawerBuyNow.disabled = false;
    }
  }

  updateCartCount(count) {
    this.cartCountNodes.forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });
    if (window.AyuTheme?.setCartCount) {
      window.AyuTheme.setCartCount(count);
    }
  }

  populateConfirmation() {
    this.confirmationTitle.textContent = this.productTitle;
    this.confirmationMeta.textContent = this.state.selections.filter(Boolean).join(' / ');
    const activeImage =
      this.root.querySelector('[data-product-media-item][data-gallery-scope="hero"].is-active img') ||
      this.root.querySelector('[data-product-media-item][data-gallery-scope="hero"]:not([hidden]) img');
    if (activeImage && this.confirmationImage) {
      this.confirmationImage.src = activeImage.currentSrc || activeImage.src;
      this.confirmationImage.alt = activeImage.alt || this.productTitle;
    }
  }
}

const productControllers = new WeakMap();

function initProductRoots(container = document) {
  container.querySelectorAll?.('[data-product-root]').forEach((root) => {
    if (productControllers.has(root)) return;
    productControllers.set(root, new ProductPurchaseController(root));
  });
}

function destroyProductRoots(container = document) {
  container.querySelectorAll?.('[data-product-root]').forEach((root) => {
    const controller = productControllers.get(root);
    if (!controller) return;
    controller.destroy();
    productControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductRoots(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initProductRoots(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyProductRoots(event.target);
});
